import { Link } from 'react-router-dom'
import { Eye } from 'lucide-react'
import { CreditStatusBadge } from '@/components/billing/credit-status-badge'
import { ProfileStatCards } from '@/components/profile/profile-stat-cards'
import { useMemberInvoices } from '@/hooks/use-billing'
import { creditLeft, creditReason } from '@/lib/credit-notes'
import { formatKES } from '@/lib/utils'

function formatDate(unixSeconds?: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function MemberCreditNotesPanel() {
  const { data, isLoading } = useMemberInvoices({ invoiceType: 'credit_note' })
  // Voided credit notes were staff corrections — members don't need to see them
  const creditNotes = (data?.data ?? []).filter((c) => c.status !== 'voided')

  const totalAvailable =
    Math.round(creditNotes.reduce((sum, c) => sum + creditLeft(c), 0) * 100) / 100

  return (
    <div className="space-y-4">
      <ProfileStatCards
        stats={[
          {
            label: 'Credit on your account',
            value: formatKES(totalAvailable),
            sub:
              totalAvailable > 0
                ? 'This will be taken off your next bill'
                : 'You have no unused credit',
            variant: totalAvailable > 0 ? 'success' : 'neutral',
          },
        ]}
      />

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : creditNotes.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No credit on your account yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-border-2">
          <table className="w-full font-sans text-sm">
            <thead>
              <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">What it's for</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Used</th>
                <th className="px-4 py-3">Left</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {creditNotes.map((c) => (
                <tr key={c.id} className="border-b border-border-2 last:border-0">
                  <td className="px-4 py-3 text-ink">{formatDate(c.createdAt)}</td>
                  <td className="px-4 py-3 text-ink">
                    {creditReason(c)}
                    <span className="block text-xs text-muted-foreground">{c.invoiceNumber}</span>
                  </td>
                  <td className="px-4 py-3 text-ink">{formatKES(Math.abs(c.amount))}</td>
                  <td className="px-4 py-3 text-ink">
                    {c.amountPaid > 0 ? formatKES(c.amountPaid) : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink">
                    {creditLeft(c) > 0 ? formatKES(creditLeft(c)) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <CreditStatusBadge credit={c} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/bills/${c.id}`}
                      className="inline-flex h-8 items-center justify-center gap-1.5 rounded border border-border bg-transparent px-3.5 font-sans text-xs font-medium tracking-wide text-ink transition-colors hover:border-ink"
                    >
                      <Eye size={14} />
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
